import { queryOptions } from '@tanstack/react-query';

import { getAuthHeaders } from '@/identity.js';

/**
 * Insights report viewer API. conversation-service (the BFF) authorizes
 * per-user via the workflow-run binding and proxies the HTML report from
 * cti-insights-control. We fetch the raw HTML here and render it in a
 * sandboxed iframe — never a raw presigned S3 URL.
 */

const ENDPOINT = '/v1/insights/report';

/**
 * Status surfaced to the viewer when the report can't be shown. `missing` is the PDF lane's
 * own: a 404 there means the run predates PDF export, while 401/403 mean the session or the
 * access went away — two different sentences to a reader, so they cannot share a kind.
 */
export type ReportError = 'forbidden' | 'missing' | 'unavailable';

export class ReportFetchError extends Error {
  constructor(readonly kind: ReportError) {
    super(kind);
    this.name = 'ReportFetchError';
  }
}

/** Platform-JWT bearer (shared) + HTML accept for the report proxy. */
async function authHeaders(): Promise<Record<string, string>> {
  return { Accept: 'text/html', ...(await getAuthHeaders()) };
}

/** Platform-JWT bearer (shared) + PDF accept for the report-PDF proxy. */
async function pdfAuthHeaders(): Promise<Record<string, string>> {
  return { Accept: 'application/pdf', ...(await getAuthHeaders()) };
}

/** The report URL, scoped to a workspace when one is selected (snake_case `workspace_id`). */
function reportUrl(runId: string, workspaceId?: string | null): string {
  const path = `${ENDPOINT}/${encodeURIComponent(runId)}`;
  if (!workspaceId) return path;
  return `${path}?${new URLSearchParams({ workspace_id: workspaceId }).toString()}`;
}

/** The report-PDF URL: same route as {@link reportUrl}, plus `format=pdf`. */
function reportPdfUrl(runId: string, workspaceId?: string | null): string {
  const path = `${ENDPOINT}/${encodeURIComponent(runId)}`;
  const params = new URLSearchParams({ format: 'pdf' });
  if (workspaceId) params.set('workspace_id', workspaceId);
  return `${path}?${params.toString()}`;
}

/**
 * The filename a report-PDF response's `Content-Disposition` header names, or `fallback` when the
 * header is missing or unparsable. Shared with `lib/api/automation-shares.ts`'s
 * `fetchSharedReportPdf`, which serves the same PDF through the share proxy.
 */
export function filenameFromContentDisposition(
  res: Response,
  fallback: string,
): string {
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^";]+)"?/.exec(disposition);
  return match?.[1] ?? fallback;
}

/** Fetch the HTML report for `runId`, or throw a typed ReportFetchError. */
async function fetchReportHtml(
  runId: string,
  workspaceId?: string | null,
): Promise<string> {
  const headers = await authHeaders();
  const res = await fetch(reportUrl(runId, workspaceId), { headers });
  if (!res.ok) {
    // 401/403/404 → the user may not view this report; anything else → transient.
    if (res.status === 401 || res.status === 403 || res.status === 404) {
      throw new ReportFetchError('forbidden');
    }
    throw new ReportFetchError('unavailable');
  }
  return res.text();
}

/**
 * Fetch the PDF report for `runId`, as a `File` named from the response's `Content-Disposition`
 * header (falling back to `insights-report-<runId>.pdf`). `File` extends `Blob`, so a caller that
 * only needs the bytes is unaffected; a download can still read the filename off `.name`.
 * 404 → missing (the run predates PDF export), 401/403 → forbidden, anything else → unavailable.
 */
export async function fetchReportPdf(
  runId: string,
  workspaceId?: string | null,
): Promise<Blob> {
  const headers = await pdfAuthHeaders();
  const res = await fetch(reportPdfUrl(runId, workspaceId), { headers });
  if (!res.ok) {
    if (res.status === 404) throw new ReportFetchError('missing');
    if (res.status === 401 || res.status === 403) {
      throw new ReportFetchError('forbidden');
    }
    throw new ReportFetchError('unavailable');
  }
  const bytes = await res.blob();
  const filename = filenameFromContentDisposition(
    res,
    `insights-report-${runId}.pdf`,
  );
  return new File([bytes], filename, { type: 'application/pdf' });
}

/** TanStack Query factory for a single run's HTML report, scoped to the selected workspace. */
export function reportQueryOptions(runId: string, workspaceId?: string | null) {
  return queryOptions({
    queryKey: ['insights-report', runId, workspaceId ?? 'all'],
    queryFn: () => fetchReportHtml(runId, workspaceId),
    // Reports are immutable once generated; avoid refetch churn.
    staleTime: 5 * 60_000,
    retry: false,
  });
}
